<template>
  <div role="tabpanel" class="tab-pane col-sm-12" id="settings">
    <h3>Settings</h3>
    <div style="margin: 2rem 0">
      <div class="input-wrapper">
        <p>Custom Framework <br />(will override any team level framework)</p>
        <cms-select
          v-if="frameworkModel"
          :initial="frameworkModel.customFrameworkId || ''"
          type="cms-custom-strength-mapping"
          @on-selection-event="onSelectEvent"
        />
      </div>
      <hr />
      <div class="input-wrapper" v-if="frameworkModel">
        <span>Display custom values to viewpoints respondents</span>
        <label class="switch">
          <input
            type="checkbox"
            @change="onToggleEvent"
            :checked="updatedFrameworkModel.isViewpoints"
          />
          <span class="slider round" />
        </label>
      </div>
      <hr />
    </div>
    <SubmitButton
      style="width: 90px"
      @click.native="saveSettings"
      :is-submitted="settingsUpdateIsLoading"
      button-class="btn-default"
      :use-recaptcha="false"
      :disabled="settingsUpdateIsLoading"
      >Save
    </SubmitButton>
    <div style="height: 10px" />
    <span v-if="settingsUpdatedSuccess" class="text-success">
      Settings saved <span class="glyphicon glyphicon-ok"></span>
    </span>
    <span v-else-if="settingsUpdatedSuccess === false" class="error-message">
      Something went wrong, please refresh the page!
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect } from "vue";
import SubmitButton from "../../submit-button.vue";
import CmsSelect from "../../cms/cms-select.vue";
import { CustomFrameworkForOrganisationsApiModel } from "../../../api-models";

const props = defineProps<{
  frameworkModel: CustomFrameworkForOrganisationsApiModel;
  settingsUpdateIsLoading: boolean;
  settingsUpdatedSuccess?: boolean;
}>();

const emit = defineEmits(["save-settings"]);

const updatedFrameworkModel = ref<CustomFrameworkForOrganisationsApiModel>(
  props.frameworkModel ?? {
    customFrameworkId: "",
    isViewpoints: false,
  },
);

watchEffect(() => {
  updatedFrameworkModel.value = props.frameworkModel;
});

const onSelectEvent = (id: string): void => {
  updatedFrameworkModel.value.customFrameworkId = id;
};

const onToggleEvent = (): void => {
  updatedFrameworkModel.value.isViewpoints = !updatedFrameworkModel.value.isViewpoints;
};

const saveSettings = (): void => {
  emit("save-settings", updatedFrameworkModel.value);
};
</script>

<style scoped lang="less">
@import (reference) "../../../styles/bootstrap/variables.less";

.input-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;

  p {
    margin: 0;
  }
}

hr {
  margin-left: 0;
  margin-right: 0;
  border-color: @practical-color;
}

/* The switch - the box around the slider */
.switch {
  position: relative;
  display: inline-block;
  width: 7rem;
  height: 34px;
}

/* Hide default HTML checkbox */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* The slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #d9d9d9;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: @transformational-color;
}

input:focus + .slider {
  box-shadow: 0 0 1px #2196f3;
}

input:checked + .slider:before {
  -webkit-transform: translateX(3.6rem);
  -ms-transform: translateX(3.6rem);
  transform: translateX(3.6rem);
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
  width: 7rem;
}

.slider.round:before {
  border-radius: 50%;
}

select {
  color: @brand-primary;
  border-color: @practical-color;
  border-radius: 5px;
  padding: 2px;
}

.error-message {
  margin: 1rem 0;
  color: red;
}
</style>
